import {
  DEFAULT_CONSTRAINTS,
  DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS,
  DEFAULT_TIME_RANGE,
  useMetricComputations,
} from '../';
import { BuiltInDimension, Measure, SortType } from '@fullstory/proto/fs/api/metrics';
import { Scope } from '@fullstory/proto/fs/api/search';
import { Constraints, TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { SortOrder } from '@fullstory/proto/fs/services/alexa';
import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { useMemo } from 'react';

/** Returns the 'top url' for the org in context. If the top url is 'Not defined'
 * it will return the second top url. A top url is the url with most visits in the org.
 * @param timeRange The time range to use for the metric search, defaults to past 7 days.
 * @param constraints The constraints applied to the metric search, minus the given time range.
 */
export const useTopUrlMetric = (
  timeRange: TimeRange = DEFAULT_TIME_RANGE,
  constraints: Omit<Constraints, 'timeRange'> = DEFAULT_CONSTRAINTS,
) => {
  const topUrlMetric: Metric = useMemo(
    () => ({
      id: '',
      name: '',
      description: '',
      metric: {
        single: {
          event: {
            primary: {
              visitedUrl: {
                any: {},
              },
            },
            deps: [],
          },
          aggregation: {
            unique: 1,
          },
          rollingWindow: 0,
          rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
          rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
        },
      },
      dimensionality: {
        ...DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS(true),
        segment: {
          segmentId: 'everyone',
          segmentScope: Scope.SCOPE_SESSION,
          segmentDefinition: {
            userProperties: [],
            excludeUserProperties: [],
            behaviors: [],
            excludeBehaviors: [],
            constraints: {
              ...constraints,
              timeRange,
            },
            excludeConstraints: null,
          },
        },
        timeRange,
        dimensionSettings: {
          sortOrder: SortOrder.SORT_ORDER_DESC,
          sortByDefault: false,
          sortType: SortType.SORT_TYPE_INVALID,
          // We only want the top page, but we request the top two in case the first one is "Not defined"
          limit: 2,
          computeTopLevel: false,
          computeNumGroups: false,
          allowAllPartialResults: false,
          noFallback: false,
          dimensions: [
            {
              builtInProperty: BuiltInDimension.DIMENSION_URL,
              prefix: '',
              limit: 0,
              computeAtLevel: false,
              eventRefinement: null,
            },
          ],
          dimension: {
            builtInProperty: BuiltInDimension.DIMENSION_URL,
            prefix: '',
            limit: 0,
            computeAtLevel: false,
            eventRefinement: null,
          },
        },
      },
      funnelFilter: null,
    }),
    [constraints, timeRange],
  );

  const { results } = useMetricComputations(topUrlMetric, true, 'top-urls-past-week', false);
  const topUrl = useMemo(() => {
    const group = results[0]?.table[0]?.groups[0];
    return group ? group.groupId : '';
  }, [results]);
  return topUrl;
};
